import {
  computed,
  inject,
  Injectable,
  InjectionToken,
  signal,
} from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

export enum ColorScheme {
  Light = 'light',
  Dark = 'dark',
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ColorScheme {
  export function opposite(value: ColorScheme): ColorScheme {
    return value === ColorScheme.Light ? ColorScheme.Dark : ColorScheme.Light;
  }
}

export interface ThemeOptions {
  /**
   * Key used to persist the user's preference in localStorage.
   * @default 'ui-color-scheme'
   */
  storageKey?: string;
  /**
   * Scheme applied when the user has not stored a preference.
   * @default 'light'
   */
  defaultScheme?: ColorScheme;
  /**
   * Brand/theme-color values to inject into `<meta name="theme-color">` and
   * `<meta name="msapplication-TileColor">` for each scheme.
   */
  themeColor?: { light: string; dark: string };
}

export const THEME_OPTIONS = new InjectionToken<ThemeOptions>('THEME_OPTIONS', {
  factory: () => ({}),
});

/**
 * Applies a color scheme to the document root and keeps the user's preference
 * in localStorage. The service is application-agnostic: brand colors and the
 * storage key are supplied via THEME_OPTIONS.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly options = inject(THEME_OPTIONS);

  readonly colorScheme = signal(ColorScheme.Light);
  readonly inactiveTheme = computed(() => ColorScheme.opposite(this.colorScheme()));

  private readonly storageKey = this.options?.storageKey ?? 'ui-color-scheme';

  start(): void {
    const view = this.document.defaultView;
    if (!view) {
      this.applyScheme(ColorScheme.Light);
      return;
    }

    this.applyScheme(this.resolveInitialScheme());

    this.meta.updateTag({
      name: 'color-scheme',
      content: `${ColorScheme.Light} ${ColorScheme.Dark}`,
    });

    const html = this.document.documentElement;
    html.style.setProperty('--safe-area-inset-top', 'env(safe-area-inset-top)');
    html.style.setProperty(
      '--safe-area-inset-bottom',
      'env(safe-area-inset-bottom)'
    );
    html.style.setProperty('--safe-area-inset-left', 'env(safe-area-inset-left)');
    html.style.setProperty(
      '--safe-area-inset-right',
      'env(safe-area-inset-right)'
    );
  }

  setColorScheme(scheme: ColorScheme): void {
    const view = this.document.defaultView;
    if (view) {
      view.localStorage.setItem(this.storageKey, scheme);
    }
    this.applyScheme(scheme);
  }

  toggleColorScheme(): void {
    this.setColorScheme(ColorScheme.opposite(this.colorScheme()));
  }

  private applyScheme(scheme: ColorScheme): void {
    this.colorScheme.set(scheme);
    this.document.documentElement.setAttribute('color-scheme', scheme);
    this.document.documentElement.setAttribute('data-theme', scheme);

    const colors = this.options?.themeColor;
    if (colors) {
      const themeColor = scheme === ColorScheme.Dark ? colors.dark : colors.light;
      this.meta.updateTag({ name: 'theme-color', content: themeColor });
      this.meta.updateTag({
        name: 'msapplication-TileColor',
        content: themeColor,
      });
    }
  }

  private resolveInitialScheme(): ColorScheme {
    const view = this.document.defaultView;
    if (!view) {
      return this.options?.defaultScheme ?? ColorScheme.Light;
    }

    const stored = view.localStorage.getItem(this.storageKey);
    if (stored === ColorScheme.Light || stored === ColorScheme.Dark) {
      return stored;
    }

    return this.options?.defaultScheme ?? ColorScheme.Light;
  }
}
